import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { theme } from '../theme';
import { navigationItems } from '../data/navigation';
import { useTransition } from '../contexts/TransitionContext';

const HeaderContainer = styled.header<{ $hidden?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: grid;
  grid-template-columns: 1fr auto 1fr auto; /* Logo centré avec sections de chaque côté + marketplace */
  align-items: center;
  padding: 12px 5%;
  width: 100%;
  min-height: 88px;
  background: ${theme.colors.white};
  box-shadow: 0px 0px 14.1px rgba(0, 0, 0, 0.16);
  box-sizing: border-box;
  gap: 0px; /* Pas d'espacement entre les sections et le logo */

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    grid-template-columns: 1fr auto 1fr;
    justify-items: center;
    align-items: center;
    padding: 12px 4%;
    min-height: auto;
    display: ${props => props.$hidden ? 'none' : 'grid'};
  }

  @media (max-width: 480px) {
    padding: 10px 3%;
  }
`;

const HamburgerIcon = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    grid-column: 3;
    justify-self: end;
    
    span {
      display: block;
      width: 24px;
      height: 2px;
      background-color: ${theme.colors.outchou_blue};
      border-radius: 2px;
      transition: all 0.3s ease;
    }
    
    &:hover span {
      background-color: ${theme.colors.outchou_green};
    }
  }
  
  @media (max-width: 480px) {
    span {
      width: 20px;
    }
  }
`;

const MobileLogoPlaceholder = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    width: 24px;
    grid-column: 1;
    justify-self: start;
  }
  
  @media (max-width: 480px) {
    width: 20px;
  }
`;

const LeftSide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px; /* Très réduit */
  justify-content: flex-end; /* Aligner à droite pour rapprocher du logo */
  grid-column: 1; /* Première colonne */
  padding-right: 0; /* Pas de padding pour coller au logo */
  margin-right: -50px; /* Marge négative importante pour rapprocher */
  position: relative;
  z-index: 2; /* Au-dessus du logo pour permettre les clics */

  @media (max-width: 768px) {
    display: none; /* Masquer sur mobile */
  }
`;

const Logo = styled.div<{ $clickable?: boolean }>`
  width: 280px;
  height: 100px;
  background: url('/shared_assets/images/Logo site.jpeg') center/contain no-repeat;
  flex-shrink: 0;
  grid-column: 2; /* Deuxième colonne - centré */
  justify-self: center; /* Centrer le logo dans sa colonne */
  position: relative;
  z-index: 1; /* Logo en arrière-plan */
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    width: 240px;
    height: 86px;
    grid-column: 2;
    justify-self: center;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    transition: transform 0.2s ease, opacity 0.2s ease;
    
    ${props => props.$clickable && `
      animation: clickablePulse 30s ease-in-out infinite;
      
      &:active {
        transform: scale(0.95);
        opacity: 0.8;
      }
      
      @keyframes clickablePulse {
        0%, 100% {
          transform: scale(1);
        }
        1% {
          transform: scale(1.08);
        }
        2% {
          transform: scale(1);
        }
        3% {
          transform: scale(1.06);
        }
        4% {
          transform: scale(1);
        }
        5%, 98% {
          transform: scale(1);
        }
      }
    `}
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 72px;
  }
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start; /* Aligner à gauche pour rapprocher du logo */
  align-items: center;
  gap: 24px; /* Très réduit */
  grid-column: 3; /* Troisième colonne */
  padding-left: 0; /* Pas de padding pour coller au logo */
  margin-left: -70px; /* Marge négative importante pour rapprocher */
  position: relative;
  z-index: 2; /* Au-dessus du logo pour permettre les clics */

  @media (max-width: 768px) {
    display: none; /* Masquer sur mobile */
  }
`;

const MarketplaceSide = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  grid-column: 4; /* Quatrième colonne - tout à droite */
  margin-left: auto; /* Pousser le marketplace tout à droite */

  @media (max-width: 768px) {
    display: none; /* Masquer sur mobile */
  }
`;

// Menu modal mobile
const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$isOpen ? 'rgba(255, 255, 255, 0.98)' : 'transparent'};
  z-index: 9999;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: flex-start;
  justify-content: center;
  padding-top: 20px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
`;

const MobileMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  max-width: 90%;
  padding: 0px 20px 40px 20px;
  pointer-events: none;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  height: fit-content;
  max-height: 100vh;
  overflow-y: auto;
  
  > * {
    pointer-events: auto;
  }
  
  @media (max-width: 480px) {
    padding: 0px 16px 32px 16px;
    max-width: 90%;
  }
`;

const MobileMenuLogo = styled.div`
  width: 240px;
  height: 86px;
  background: url('/shared_assets/images/Logo site.jpeg') center/contain no-repeat;
  margin-bottom: 24px;
  margin-top: 10px;
  
  @media (max-width: 480px) {
    width: 200px;
    height: 72px;
    margin-bottom: 20px;
  }
`;

const MobileMenuTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  color: ${theme.colors.outchou_blue};
  margin: 20px 0 16px 0;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
    margin-top: 16px;
    margin-bottom: 12px;
  }
`;

const MobileMenuGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-top: 0px;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const MobileMenuRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 12px;
  
  @media (max-width: 480px) {
    margin-top: 20px;
    gap: 10px;
  }
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  font-family: ${theme.fonts.title};
  font-weight: ${props => props.$active ? 700 : 500};
  font-size: 22px;
  line-height: 32px;
  text-transform: uppercase;
  color: ${theme.colors.outchou_blue};
  text-decoration: ${props => props.$active ? 'underline' : 'none'};
  cursor: pointer;
  transition: color 0.3s ease;
  text-align: center;
  padding: 12px 20px;

  &:hover {
    color: ${theme.colors.outchou_green};
  }

  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 28px;
    padding: 10px 16px;
  }
`;

const MobileMarketplaceLink = styled(Link)<{ $active?: boolean }>`
  font-family: ${theme.fonts.title};
  font-weight: ${props => props.$active ? 700 : 500};
  font-size: 22px;
  line-height: 32px;
  text-transform: uppercase;
  color: ${theme.colors.outchou_blue};
  text-decoration: ${props => props.$active ? 'underline' : 'none'};
  cursor: pointer;
  transition: color 0.3s ease;
  text-align: center;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    color: ${theme.colors.outchou_green};
  }

  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 28px;
    padding: 10px 16px;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  font-family: ${theme.fonts.title};
  font-weight: ${props => props.$active ? 700 : 500};
  font-size: 16px;
  line-height: 19px;
  text-transform: uppercase;
  color: ${theme.colors.outchou_blue};
  text-decoration: ${props => props.$active ? 'underline' : 'none'};
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;
  position: relative;
  z-index: 3; /* Au-dessus du logo pour garantir les clics */

  &:hover {
    color: ${theme.colors.outchou_blue_hover};
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 17px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 15px;
  }
`;

const ShoppingCartIcon = styled(Link)<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }

  svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: ${props => props.$active ? theme.colors.outchou_green : theme.colors.outchou_blue};
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: stroke 0.3s ease;
  }

  &:hover svg {
    stroke: ${theme.colors.outchou_green_hover};
  }

  @media (max-width: 768px) {
    svg {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 480px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

// Video Modal pour le logo mobile
const LogoVideoModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 999999;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const LogoVideoModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  background: black;
  border-radius: 8px;
  overflow: hidden;
`;

const LogoVideoModalVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

const LogoVideoCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid white;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  &::before {
    content: '×';
    line-height: 1;
  }
`;

const Header: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoVideoOpen, setIsLogoVideoOpen] = useState(false);
  const logoVideoRef = useRef<HTMLVideoElement>(null);
  const { setShouldAnimate } = useTransition();
  const [isMobile, setIsMobile] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Header toujours visible sur toutes les pages (mobile et desktop)
  useEffect(() => {
    setIsHeaderHidden(false);
  }, [isMobile, location.pathname]);

  const handleHeaderLinkClick = () => {
    setShouldAnimate(true);
    setIsMobileMenuOpen(false); // Fermer le menu mobile après clic
  };

  const handleLogoClick = () => {
    setIsLogoVideoOpen(true);
  };

  const handleCloseLogoVideo = () => {
    setIsLogoVideoOpen(false);
    if (logoVideoRef.current) {
      logoVideoRef.current.pause();
      logoVideoRef.current.currentTime = 0;
    }
  };

  // Démarrer la vidéo quand la modal s'ouvre
  useEffect(() => {
    if (isLogoVideoOpen && logoVideoRef.current) {
      logoVideoRef.current.play().catch(() => {});
    }
  }, [isLogoVideoOpen]);

  // Écouter l'événement personnalisé pour ouvrir le menu depuis le hero
  useEffect(() => {
    const handleOpenMobileMenu = () => {
      setIsMobileMenuOpen(true);
    };

    window.addEventListener('openMobileMenu', handleOpenMobileMenu);
    return () => window.removeEventListener('openMobileMenu', handleOpenMobileMenu);
  }, []);

  const renderNavigationItem = (item: any) => {
    // Handle marketplace with shopping cart icon
    if (item.isIcon && item.id === 'marketplace') {
      return (
        <ShoppingCartIcon 
          key={item.id}
          to={item.path}
          $active={location.pathname === item.path}
          title="Marketplace"
        >
          <svg viewBox="0 0 24 24">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
        </ShoppingCartIcon>
      );
    }

    // Regular navigation link
    return (
      <NavLink 
        key={item.id} 
        to={item.path}
        $active={location.pathname === item.path}
        onClick={handleHeaderLinkClick}
      >
        {item.label}
      </NavLink>
    );
  };
  
  // Séparer les éléments de navigation selon l'ordre alphabétique
  // Ordre alphabétique: Accueil, Gastronomie, Histoire, Peuples, Relief & Climat, Tradition, Le Hanout
  const leftItems = navigationItems.slice(0, 3); // Accueil, Gastronomie, Histoire
  const rightItems = navigationItems.slice(3, 6); // Peuples, Relief & Climat, Tradition
  const marketplaceItem = navigationItems.find(item => item.id === 'marketplace'); // Le Hanout tout à droite

  // Pour le menu mobile : pages en 2 colonnes (sauf marketplace)
  const mobilePages = navigationItems.filter(item => item.id !== 'marketplace');
  const mobileCol1 = mobilePages.slice(0, 3); // Accueil, Gastronomie, Histoire
  const mobileCol2 = mobilePages.slice(3, 6); // Peuples, Relief & Climat, Tradition

  return (
    <>
      <HeaderContainer $hidden={isHeaderHidden}>
        <MobileLogoPlaceholder />
        <LeftSide>
          {leftItems.map(renderNavigationItem)}
        </LeftSide>
        <Logo $clickable={isMobile} onClick={handleLogoClick} />
        <RightSide>
          {rightItems.map(renderNavigationItem)}
        </RightSide>
        {marketplaceItem && (
          <MarketplaceSide>
            {renderNavigationItem(marketplaceItem)}
          </MarketplaceSide>
        )}
        <HamburgerIcon onClick={() => setIsMobileMenuOpen(true)} aria-label="Ouvrir le menu">
          <span></span>
          <span></span>
          <span></span>
        </HamburgerIcon>
      </HeaderContainer>

      {/* Menu mobile */}
      <MobileMenuOverlay 
        $isOpen={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <MobileMenuContent onClick={(e) => e.stopPropagation()}>
          {/* Logo en haut */}
          <MobileMenuLogo />
          
          {/* Liste en une colonne */}
          <MobileMenuGrid>
            {mobilePages.map((item) => (
              <MobileNavLink
                key={item.id}
                to={item.path}
                $active={item.path === '/' ? location.pathname === '/' : (location.pathname === item.path || location.pathname.startsWith(item.path + '/'))}
                onClick={handleHeaderLinkClick}
              >
                {item.label}
              </MobileNavLink>
            ))}
            
            {/* Le Hanout */}
            {marketplaceItem && (
              <MobileMarketplaceLink
                to={marketplaceItem.path}
                $active={location.pathname === marketplaceItem.path}
                onClick={handleHeaderLinkClick}
              >
                {marketplaceItem.label}
                <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: 'none', stroke: '#1B3A4B', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
              </MobileMarketplaceLink>
            )}
          </MobileMenuGrid>
        </MobileMenuContent>
      </MobileMenuOverlay>

      {/* Modal vidéo pour le logo mobile */}
      <LogoVideoModal $isOpen={isLogoVideoOpen} onClick={handleCloseLogoVideo}>
        <LogoVideoModalContent onClick={(e) => e.stopPropagation()}>
          <LogoVideoCloseButton onClick={handleCloseLogoVideo} />
          <LogoVideoModalVideo
            ref={logoVideoRef}
            src="/shared_assets/videos/accueil.mp4"
            controls
            playsInline
          />
        </LogoVideoModalContent>
      </LogoVideoModal>
    </>
  );
};

export default Header; 
